import type { SharedResourceRefusal, SharedResourceSignedRequestResult } from 'shared/download/sharedResourceSignedRequest';
import React, {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { SharedResourceDownloadContextType, SharedResourceType } from './types';
import { useMountedRef } from 'hooks/useMountRef';
import useFormEdit from 'views/issue/forms/useFormEdit';
import { useServiceWorkerReady } from 'hooks/useServiceWorkerReady';
import { downloadZip } from './downloadZip';
import { fetchSharedResourceSignedRequest } from 'shared/download/fetchSharedResourceSignedRequest';
import { logInfo } from 'setup/logger/logInfo';
import { requestSignedRequestThroughServiceWorker, toSharedResourceRequest } from './sharedResourceRequest';

export const PUBLIC_SW_WAIT_MS = 10000;

type DownloadTransport = 'serviceWorker' | 'directHttp';

function getInitialTransport(): DownloadTransport {
  return typeof navigator !== 'undefined' && 'serviceWorker' in navigator
    ? 'serviceWorker'
    : 'directHttp';
}

const SharedResourceDownloadContext = React.createContext<
  SharedResourceDownloadContextType | undefined
>(undefined);

const recentlyTriggeredDownloads = new Set<string>();

const markAndCheckRecentDownload = (key: string): boolean => {
  if (recentlyTriggeredDownloads.has(key)) {
    return true;
  }

  recentlyTriggeredDownloads.add(key);
  setTimeout(() => {
    recentlyTriggeredDownloads.delete(key);
  }, 60 * 1000);

  return false;
};

const WithSharedResourceDownload: React.FC<{
  entityType: SharedResourceType;
  children?: ReactNode;
}> = ({ entityType, children }) => {
  const { setEditing } = useFormEdit();
  const mountedRef = useMountedRef();
  const serviceWorkerReady = useServiceWorkerReady();
  const serviceWorkerReadyRef = useRef(serviceWorkerReady);
  serviceWorkerReadyRef.current = serviceWorkerReady;
  const [transport, setTransport] =
    useState<DownloadTransport>(getInitialTransport);
  const [statusCode, setStatusCode] = useState<number | undefined>();
  const [refusal, setRefusal] = useState<SharedResourceRefusal | undefined>();
  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const [signedRequest, setSignedRequest] = useState<
    string | string[] | undefined
  >();
  const [resourceId, setResourceId] = useState<string | undefined>();
  const [issueId, setIssueId] = useState<string | undefined>();
  const [eventId, setEventId] = useState<string | undefined>();
  const [incomingEmailMessageId, setIncomingEmailMessageId] = useState<string | undefined>();
  const [linkedDocumentationId, setLinkedDocumentationId] = useState<string | undefined>();
  const [token, setToken] = useState<string | undefined>();

  const startDownloadSharedResource = useCallback(
    ({
      reportId,
      documentId,
      issueId,
      eventId,
      incomingEmailMessageId,
      linkedDocumentationId,
      token,
    }: {
      reportId: string | undefined;
      documentId: string | undefined;
      issueId: string | undefined;
      eventId: string | undefined;
      incomingEmailMessageId: string | undefined;
      linkedDocumentationId: string | undefined;
      token: string;
    }) => {
      const nextResourceId = reportId || documentId || linkedDocumentationId;

      setResourceId(nextResourceId);
      setIssueId(issueId);
      setEventId(eventId);
      setIncomingEmailMessageId(incomingEmailMessageId);
      setLinkedDocumentationId(linkedDocumentationId);
      setToken(token);
      setIsDownloading(true);
      // A new download has no outcome yet: neither its status nor the reason of a refusal.
      setStatusCode(undefined);
      setRefusal(undefined);
    },
    []
  );

  const request = useMemo(
    () =>
      toSharedResourceRequest(entityType, {
        resourceId,
        token,
        issueId,
        eventId,
        incomingEmailMessageId,
        linkedDocumentationId,
      }),
    [entityType, resourceId, token, issueId, eventId, incomingEmailMessageId, linkedDocumentationId]
  );

  const fallBackToDirectHttp = useCallback(() => {
    logInfo('shared-download-sw-fallback', {
      entityType,
      serviceWorkerReady: serviceWorkerReadyRef.current,
      waitMs: PUBLIC_SW_WAIT_MS,
    });
    setTransport('directHttp');
  }, [entityType]);

  // Incognito often has no working SW: fall back if it is not ready or does not answer in time.
  useEffect(() => {
    if (!resourceId || statusCode !== undefined || transport !== 'serviceWorker') {
      return;
    }

    const timeout = setTimeout(fallBackToDirectHttp, PUBLIC_SW_WAIT_MS);

    return (): void => {
      clearTimeout(timeout);
    };
  }, [resourceId, statusCode, transport, fallBackToDirectHttp]);

  // The transport a request can go through now: none while the SW is not ready yet.
  const requestTransport: DownloadTransport | undefined =
    transport === 'directHttp' || serviceWorkerReady ? transport : undefined;
  const hasAnswer = statusCode !== undefined;

  useEffect(() => {
    if (!request || !requestTransport || hasAnswer) {
      return;
    }

    const abortController = new AbortController();
    const applyResult = (result: SharedResourceSignedRequestResult): void => {
      if (!mountedRef.current || abortController.signal.aborted) {
        return;
      }
      setSignedRequest(result.signedRequest);
      setRefusal(result.refusal);
      setStatusCode(result.statusCode);
      setIsDownloading(false);
    };

    const isServiceWorkerTransport = requestTransport === 'serviceWorker';
    const signedRequestResult = isServiceWorkerTransport
      ? requestSignedRequestThroughServiceWorker(request)
      : fetchSharedResourceSignedRequest(request, abortController.signal);

    signedRequestResult.then(applyResult).catch(() => {
      if (isServiceWorkerTransport && !abortController.signal.aborted) {
        fallBackToDirectHttp();
      }
    });

    return (): void => {
      abortController.abort();
    };
  }, [request, requestTransport, hasAnswer, mountedRef, fallBackToDirectHttp]);

  const onZipDone = useCallback(() => {
    setEditing(false, 'reportDownload');
  }, [setEditing]);

  const downloadSignedRequest = useCallback(() => {
    if (!signedRequest) {
      return;
    }

    const dedupeKey =
      resourceId && token
        ? `${entityType}:${resourceId}:${token}`
        : Array.isArray(signedRequest)
        ? signedRequest.join('|')
        : signedRequest;

    if (markAndCheckRecentDownload(dedupeKey)) {
      return;
    }

    if (typeof signedRequest === 'string') {
      const link = document.createElement('a');
      link.href = signedRequest;
      link.dispatchEvent(new MouseEvent('click'));
      return;
    }

    if (Array.isArray(signedRequest)) {
      setEditing(true, 'reportDownload');
      downloadZip(signedRequest, onZipDone);
      return;
    }
  }, [signedRequest, onZipDone, setEditing, entityType, resourceId, token]);

  useEffect(() => {
    if (!signedRequest) {
      return;
    }

    downloadSignedRequest();
  }, [signedRequest, downloadSignedRequest]);

  const ctx = {
    statusCode,
    refusal,
    startDownloadSharedResource: startDownloadSharedResource,
    isDownloading,
    downloadSignedRequest,
    entityType,
  };

  return (
    <SharedResourceDownloadContext.Provider value={ctx}>
      {children}
    </SharedResourceDownloadContext.Provider>
  );
};

function useSharedResourceDownload(): SharedResourceDownloadContextType {
  const context = React.useContext(SharedResourceDownloadContext);
  if (context === undefined) {
    throw new Error(
      'useSharedResourceDownload must be used within a SharedResourceDownloadContextProvider'
    );
  }
  return context;
}

export {
  useSharedResourceDownload,
  WithSharedResourceDownload,
};
